import { toast } from '@affine/component';
import { defaultWorkspaceApiClient } from '@affine/core/modules/cloud';
import { mimeTypeFromFilename } from '@affine/core/utils/blob-file-type';
import { I18n } from '@affine/i18n';

import type { WorkspaceOpenOptions } from '../open-options';

export type DemoSampleSelection =
  | { entry: string; intent: string }
  | { mime?: string }
  | { sha256: string };

const policyCache = new Map<string, { value: boolean; expiresAt: number }>();
const preparedSamples = new WeakSet<File>();
let lastReplacementToastAt = Number.NEGATIVE_INFINITY;
const REPLACEMENT_TOAST_DEDUPE_MS = 1_500;

export function isPreparedDemoSample(file: File): boolean {
  return preparedSamples.has(file);
}

/** Select a catalog MIME using metadata only; never sniff visitor bytes. */
export function demoFileMimeHint(
  file: Pick<File, 'name' | 'type'>
): string | undefined {
  const explicit = file.type.trim();
  if (explicit && explicit !== 'application/octet-stream') return explicit;
  const extension = file.name.split('.').pop()?.toLowerCase();
  const semanticMime =
    extension === 'md' || extension === 'markdown'
      ? 'text/markdown'
      : extension === 'ics'
        ? 'text/calendar'
        : undefined;
  return (
    semanticMime ?? mimeTypeFromFilename(file.name) ?? (explicit || undefined)
  );
}

function demoLocale(): 'de' | 'en' {
  return String(I18n.language).toLowerCase().startsWith('de') ? 'de' : 'en';
}

// Demo documents and every server-owned replacement file deliberately use one
// shared English content set. UI notices remain localized through demoLocale().
const DEMO_CONTENT_LOCALE = 'en' as const;

/** One shared, deduplicated explanation for every completed Demo replacement. */
export function showDemoReplacementToast(): void {
  const now = Date.now();
  if (
    now >= lastReplacementToastAt &&
    now - lastReplacementToastAt < REPLACEMENT_TOAST_DEDUPE_MS
  )
    return;
  lastReplacementToastAt = now;
  toast(
    demoLocale() === 'de'
      ? 'Demo: Inhalt ersetzt. Wir verwenden freigegebene Beispieldaten, damit niemand ungeprüfte oder anstößige Inhalte öffentlich hochlädt.'
      : 'Demo: Content replaced. We use approved examples so no one can upload unreviewed or offensive content to the public demo.'
  );
}

export function demoTextPasteNotice(): string {
  return demoLocale() === 'de'
    ? 'Demo: Eingefügter Text wird nicht übernommen. Bitte gib einen eigenen neutralen Beispieltext ein.'
    : 'Demo: Pasted text is not inserted. Please type neutral example text instead.';
}

export function demoInputPolicyUnavailableNotice(): string {
  return demoLocale() === 'de'
    ? 'Einfügen ist gerade nicht möglich. Bitte versuche es gleich noch einmal.'
    : 'Pasting is not possible right now. Please try again in a moment.';
}

function pathFor(workspaceId: string, suffix: string): string {
  return `/api/workspace/${encodeURIComponent(workspaceId)}/demo/${suffix}`;
}

async function request(
  workspaceId: string,
  path: string,
  init?: RequestInit
): Promise<Response> {
  return defaultWorkspaceApiClient.request({
    workspaceId,
    path,
    init: { credentials: 'include', cache: 'no-store', ...init },
    category: 'workspace-api',
    key: `demo-input:${path}`,
    mode: 'foreground',
    priority: 'foreground',
  });
}

/** A failed policy lookup never falls through to an ordinary raw upload. */
export async function getWorkspaceFileInputPolicy(
  workspaceId: string,
  externalAccess?: Pick<
    NonNullable<WorkspaceOpenOptions['externalDocAccess']>,
    'filePolicy'
  >
): Promise<boolean> {
  if (externalAccess) return externalAccess.filePolicy.replaceUserFiles;
  return replacesDemoUserFiles(workspaceId);
}

/** A failed member policy lookup never permits an ordinary raw upload. */
export async function replacesDemoUserFiles(
  workspaceId: string
): Promise<boolean> {
  const cached = policyCache.get(workspaceId);
  if (cached && cached.expiresAt > Date.now()) return cached.value;
  const response = await request(workspaceId, pathFor(workspaceId, 'policy'));
  if (!response.ok) throw new Error('Demo file policy is unavailable');
  const data: unknown = await response.json();
  if (
    !data ||
    typeof data !== 'object' ||
    !('schemaVersion' in data) ||
    data.schemaVersion !== 1 ||
    !('replaceUserFiles' in data) ||
    typeof data.replaceUserFiles !== 'boolean'
  ) {
    throw new Error('Demo file policy is invalid');
  }
  // Never cache a negative response: an admin can bind the Demo plan while
  // this tab is open, and the next file action must not read/stage raw bytes
  // under a stale ordinary-workspace decision.
  if (data.replaceUserFiles) {
    policyCache.set(workspaceId, {
      value: true,
      expiresAt: Date.now() + 30_000,
    });
  } else {
    policyCache.delete(workspaceId);
  }
  return data.replaceUserFiles;
}

function sampleQuery(selection: DemoSampleSelection): URLSearchParams {
  const query = new URLSearchParams({ locale: DEMO_CONTENT_LOCALE });
  if ('intent' in selection) {
    query.set('entry', selection.entry);
    query.set('intent', selection.intent);
  } else if ('sha256' in selection) {
    query.set('sha256', selection.sha256);
  } else if (selection.mime) {
    query.set('mime', selection.mime);
  }
  return query;
}

/** Fetches only server-owned sample bytes; caller never sends original bytes. */
export async function demoSampleFile(
  workspaceId: string,
  selection: DemoSampleSelection
): Promise<File> {
  const response = await request(
    workspaceId,
    `${pathFor(workspaceId, 'sample')}?${sampleQuery(selection)}`
  );
  if (
    !response.ok ||
    response.headers.get('X-StreamLain-Demo-Replacement') !== '1'
  ) {
    throw new Error('Prepared demo file is unavailable');
  }
  const rawName = response.headers.get('X-StreamLain-Demo-Filename');
  if (!rawName) throw new Error('Prepared demo file name is unavailable');
  const name = decodeURIComponent(rawName);
  if (!name.startsWith('Demo-') || /[/\\]/.test(name)) {
    throw new Error('Prepared demo file name is invalid');
  }
  const bytes = await response.blob();
  const file = new File([bytes], name, {
    type: response.headers.get('Content-Type') ?? bytes.type,
  });
  preparedSamples.add(file);
  return file;
}

/** Materializes the same immutable sample without transferring visitor bytes. */
export async function materializeDemoSample(
  workspaceId: string,
  selection: DemoSampleSelection | { sha256: string }
): Promise<{ blobKey: string; filename: string; mime: string }> {
  const response = await request(
    workspaceId,
    pathFor(workspaceId, 'sample/materialize'),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ locale: DEMO_CONTENT_LOCALE, ...selection }),
    }
  );
  if (!response.ok) throw new Error('Prepared demo file could not be inserted');
  const data: unknown = await response.json();
  if (
    !data ||
    typeof data !== 'object' ||
    !('schemaVersion' in data) ||
    data.schemaVersion !== 1 ||
    !('blobKey' in data) ||
    typeof data.blobKey !== 'string' ||
    !('sample' in data) ||
    !data.sample ||
    typeof data.sample !== 'object' ||
    !('filename' in data.sample) ||
    typeof data.sample.filename !== 'string' ||
    !('mime' in data.sample) ||
    typeof data.sample.mime !== 'string'
  ) {
    throw new Error('Prepared demo file response is invalid');
  }
  return {
    blobKey: data.blobKey,
    filename: data.sample.filename,
    mime: data.sample.mime,
  };
}
